import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Checkbox,
  Card,
  Divider,
  message,
  Typography,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  WechatOutlined,
} from '@ant-design/icons';
import { login, getCurrentUser } from '../../services/auth';
import { saveAuthData, isAuthenticated, setUserInfo } from '../../utils/auth';
import { weChatLogin } from '../../services/wechat';
import { getErrorMessageFromUnknown } from '../../utils/errorHandler';

const { Title, Text } = Typography;

interface LoginForm {
  username: string;
  password: string;
  remember: boolean;
}

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [wechatLoading, setWechatLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated()) {
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [navigate, location]);

  // 自动检测企业微信环境并处理登录
  React.useEffect(() => {
    const autoWeChatLogin = async () => {
      // 如果已登录，不处理
      if (isAuthenticated()) {
        return;
      }

      const ua = navigator.userAgent.toLowerCase();
      const isInWeChatWork = ua.includes('wxwork');
      
      if (isInWeChatWork) {
        // 检查 URL 中是否有 code
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');

        if (code) {
          // 有 code，自动处理登录
          setWechatLoading(true);
          
          try {
            const { weChatOAuthCallback } = await import('../../services/wechat');
            const response = await weChatOAuthCallback(code);
            
            if (response.success && response.access_token) {
              saveAuthData({
                access_token: response.access_token,
                refresh_token: response.refresh_token || response.access_token,
                token_type: response.token_type || 'bearer',
              });
              
              if (response.user_info) {
                setUserInfo({
                  id: response.user_info.id,
                  username: response.user_info.username,
                  email: response.user_info.email,
                  name: response.user_info.real_name,
                  role: response.user_info.role as any,
                  avatar: response.user_info.wechat_avatar,
                  permissions: [],
                });
              }
              
              message.success('企业微信登录成功！');
              navigate('/');
            } else {
              message.error(response.message || '企业微信登录失败');
              setWechatLoading(false);
            }
          } catch (error: unknown) {
            console.error('企业微信自动登录失败:', error);
            message.error(getErrorMessageFromUnknown(error) || '企业微信登录失败');
            setWechatLoading(false);
          }
        } else {
          // 没有 code，自动跳转获取授权（静默授权）
          setWechatLoading(true);
          
          try {
            const { getWeChatOAuthUrl } = await import('../../services/wechat');
            const protocol = window.location.protocol;
            const host = window.location.host;
            const currentUrl = `${protocol}//${host}/login`;
            
            const response = await getWeChatOAuthUrl(currentUrl, 'STATE', 'snsapi_base');
            
            if (response.authorize_url) {
              window.location.href = response.authorize_url;
            } else {
              console.error('无法获取授权 URL');
              setWechatLoading(false);
            }
          } catch (error: unknown) {
            console.error('获取授权 URL 失败:', error);
            setWechatLoading(false);
          }
        }
      }
    };
    
    autoWeChatLogin();
  }, [navigate]);


  const handleSubmit = async (values: LoginForm) => {
    setLoading(true);
    try {
      const response = await login({
        username: values.username,
        password: values.password,
        remember: values.remember,
      });

      // Save authentication data
      saveAuthData(response);

      // Fetch and save user info
      try {
        const userInfo = await getCurrentUser();
        setUserInfo(userInfo);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }

      message.success('登录成功！');

      // Redirect to intended page or role-based default page
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (error: unknown) {
      console.error('Login error:', error);
      message.error(getErrorMessageFromUnknown(error) || '登录失败，请检查用户名和密码');
    } finally {
      setLoading(false);
    }
  };

  const handleWeChatLogin = async () => {
    setWechatLoading(true);
    try {
      await weChatLogin();
    } catch (error: unknown) {
      console.error('WeChat login error:', error);
      message.error(getErrorMessageFromUnknown(error) || '企业微信登录失败');
      setWechatLoading(false);
    }
    // Note: wechatLoading will be reset when page redirects or user returns
  };

  return (
    <div className="login-container">
      <Card className="login-box">
        <div className="login-logo">
          <div className="login-brand-mark">瑞</div>
          <Title level={2} className="login-brand-name">瑞象CRM</Title>
          <Text type="secondary" className="login-brand-sub">律所客户与案件管理系统</Text>
        </div>

        <Form
          form={form}
          name="login"
          className="login-form"
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="on"
          size="large"
          initialValues={{
            remember: true,
            username: '',
            password: '',
          }}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              {
                required: true,
                message: '请输入用户名！',
              },
              {
                min: 3,
                message: '用户名至少3个字符！',
              },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="用户名"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="密码"
            rules={[
              {
                required: true,
                message: '请输入密码！',
              },
              {
                min: 6,
                message: '密码至少6个字符！',
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item name="remember" valuePropName="checked">
            <Checkbox>记住我</Checkbox>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="login-button"
              loading={loading}
              block
            >
              {loading ? '登录中...' : '登录'}
            </Button>
          </Form.Item>

        </Form>

        <Divider plain className="login-divider">或</Divider>

        <Button
          icon={<WechatOutlined />}
          onClick={handleWeChatLogin}
          loading={wechatLoading}
          block
          className="wechat-login-btn"
        >
          {wechatLoading ? '跳转中...' : '企业微信登录'}
        </Button>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            如需开通账号，请联系管理员
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default Login;